import React, { useState, useEffect, useCallback } from 'react';
import { 
  Users, MessageCircle, Settings, Download, Search, 
  ExternalLink, ChevronRight, BarChart2, Loader2,
  Mail, Instagram, Link2, Copy, Check, AlertCircle,
  Sparkles, ArrowRight, Save, ToggleLeft, ToggleRight,
  Zap, Plus, Trash2, Edit2, X, Send
} from 'lucide-react';
import api from '../services/api';
import { toast } from 'react-toastify';

export default function AudienceManager() {
  const [activeTab, setActiveTab] = useState('overview');
  const [engagedUsers, setEngagedUsers] = useState([]);
  const [communityMembers, setCommunityMembers] = useState([]);
  const [communityPage, setCommunityPage] = useState(null);
  const [engagedStats, setEngagedStats] = useState(null);
  const [communityStats, setCommunityStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [featureEnabled, setFeatureEnabled] = useState(true);
  const [accounts, setAccounts] = useState([]);
  const [selectedAccountId, setSelectedAccountId] = useState(null);
  const [copied, setCopied] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [dataLoaded, setDataLoaded] = useState(false);
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);
  
  // Settings form state
  const [settingsForm, setSettingsForm] = useState({
    is_active: false,
    headline: '',
    description: '',
    button_text: 'Join'
  });
  const [savingSettings, setSavingSettings] = useState(false);
  const [creatingPage, setCreatingPage] = useState(false);

  // DM Automation state
  const [triggers, setTriggers] = useState([]);
  const [triggersLoading, setTriggersLoading] = useState(false);
  const [showTriggerModal, setShowTriggerModal] = useState(false);
  const [editingTrigger, setEditingTrigger] = useState(null);
  const [triggerForm, setTriggerForm] = useState({
    keyword: '',
    match_type: 'contains',
    action_type: 'send_message',
    dm_message: '',
    chat_flow_id: null,
    is_active: true
  });
  const [savingTrigger, setSavingTrigger] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [dmAutomationEnabled, setDmAutomationEnabled] = useState(false);
  
  // Chat flows state
  const [chatFlows, setChatFlows] = useState([]);
  const [creatingEmailFlow, setCreatingEmailFlow] = useState(false);
  const [emailFlowCreated, setEmailFlowCreated] = useState(false);
  
  // Backfill state
  const [backfilling, setBackfilling] = useState(false);
  const [needsReauth, setNeedsReauth] = useState(false);
  const [hasCommentsPermission, setHasCommentsPermission] = useState(true); // Assume true until we know otherwise
  const [hasMessagesPermission, setHasMessagesPermission] = useState(false); // DM sending permission

  // Get the community link
  const getCommunityLink = () => {
    if (!communityPage?.slug) return null;
    // Use production URL for the link
    return `https://vaulto.social/c/${communityPage.slug}`;
  };

  // Copy link to clipboard
  const copyLink = async () => {
    const link = getCommunityLink();
    if (!link) return;
    
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
      toast.success('Link copied to clipboard!');
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      toast.error('Failed to copy link');
    }
  };

  // Update settings form when community page loads
  useEffect(() => {
    if (communityPage) {
      setSettingsForm({
        is_active: communityPage.is_active || false,
        headline: communityPage.headline || 'Get exclusive updates!',
        description: communityPage.description || '',
        button_text: communityPage.button_text || 'Join'
      });
    }
  }, [communityPage]);

  // Create contact page
  const createContactPage = async () => {
    setCreatingPage(true);
    try {
      // GET /community/my-page will auto-create the page if it doesn't exist
      const response = await api.get('/community/my-page');
      setCommunityPage(response.data);
      setSettingsForm({
        is_active: response.data.is_active || false,
        headline: response.data.headline || 'Get exclusive updates!',
        description: response.data.description || '',
        button_text: response.data.button_text || 'Join'
      });
      toast.success('Contact page created! Now customize it below.');
      setActiveTab('settings');
    } catch (err) {
      console.error('Error creating contact page:', err);
      toast.error('Failed to create contact page. Please try again.');
    } finally {
      setCreatingPage(false);
    }
  };

  // Save settings
  const saveSettings = async () => {
    setSavingSettings(true);
    try {
      const response = await api.put('/community/my-page', settingsForm);
      setCommunityPage(response.data);
      toast.success('Settings saved successfully!');
    } catch (err) {
      console.error('Error saving settings:', err);
      toast.error('Failed to save settings. Please try again.');
    } finally {
      setSavingSettings(false);
    }
  };

  // Fetch triggers
  const fetchTriggers = useCallback(async () => {
    if (!selectedAccountId) return;
    
    setTriggersLoading(true);
    try {
      const response = await api.get('/triggers', { params: { account_id: selectedAccountId } });
      setTriggers(response.data || []);
      setDmAutomationEnabled(true);
    } catch (err) {
      console.log('Triggers not available:', err.message);
      setTriggers([]);
      if (err.response?.status === 403) {
        setDmAutomationEnabled(false);
      }
    } finally {
      setTriggersLoading(false);
    }
  }, [selectedAccountId]);

  // Fetch triggers when account changes
  useEffect(() => {
    if (selectedAccountId) {
      fetchTriggers();
      fetchChatFlows();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedAccountId, fetchTriggers]);

  // Fetch chat flows
  const fetchChatFlows = async () => {
    if (!selectedAccountId) return;
    try {
      const response = await api.get('/chat-flows', { params: { account_id: selectedAccountId } });
      setChatFlows(response.data || []);
      // Check if email flow exists
      const hasEmailFlow = response.data?.some(f => f.name === 'Email Collection Flow');
      setEmailFlowCreated(hasEmailFlow);
    } catch (err) {
      console.log('Chat flows not available:', err.message);
      setChatFlows([]);
    }
  };

  // Create default email collection flow
  const createEmailFlow = async () => {
    setCreatingEmailFlow(true);
    try {
      const response = await api.post('/chat-flows/create-default-email-flow', null, { 
        params: { account_id: selectedAccountId } 
      });
      toast.success('Email collection flow created!');
      setEmailFlowCreated(true);
      setChatFlows(prev => [response.data, ...prev]);
      // Automatically set the trigger to use this flow
      setTriggerForm(prev => ({
        ...prev,
        action_type: 'start_flow',
        chat_flow_id: response.data.id
      }));
    } catch (err) {
      console.error('Error creating email flow:', err);
      toast.error('Failed to create email flow');
    } finally {
      setCreatingEmailFlow(false);
    }
  };

  // Backfill engaged users from historical comments
  const backfillEngagedUsers = async () => {
    if (!selectedAccountId) return;
    
    setBackfilling(true);
    setNeedsReauth(false);
    try {
      await api.post(`/accounts/instagram-accounts/${selectedAccountId}/engaged-users/backfill`, null, {
        params: { max_posts: 50 }
      });
      toast.success('Backfill started! Your engaged users will be updated in a few minutes.');
      // Refresh data after a short delay
      setTimeout(() => {
        fetchData();
      }, 5000);
    } catch (err) {
      console.error('Error starting backfill:', err);
      // Check if it's a permission error (403)
      if (err.response?.status === 403 && err.response?.data?.detail?.includes('permission')) {
        setNeedsReauth(true);
        toast.error('Comments permission required. Please re-authorize your Instagram account.');
      } else {
        toast.error('Failed to start backfill. Please try again.');
      }
    } finally {
      setBackfilling(false);
    }
  };

  // Handle granting comments permission
  const handleGrantCommentsPermission = async () => {
    if (!selectedAccountId) return;
    
    try {
      // Call the upgrade endpoint to get the OAuth URL
      const response = await api.get(`/oauth/instagram/upgrade-comments-permission/${selectedAccountId}`);
      if (response.data.auth_url) {
        // Redirect to Instagram OAuth
        window.location.href = response.data.auth_url;
      }
    } catch (err) {
      console.error('Error initiating permission upgrade:', err);
      toast.error('Failed to initiate permission upgrade. Please try again.');
    }
  };

  // Handle granting messages permission (for DM automation)
  const handleGrantMessagesPermission = async () => {
    if (!selectedAccountId) return;
    
    try {
      // Call the upgrade endpoint to get the OAuth URL
      const response = await api.get(`/oauth/instagram/upgrade-messages-permission/${selectedAccountId}`);
      if (response.data.auth_url) {
        // Redirect to Instagram OAuth
        window.location.href = response.data.auth_url;
      }
    } catch (err) {
      console.error('Error initiating messages permission upgrade:', err);
      toast.error('Failed to initiate permission upgrade. Please try again.');
    }
  };

  // Open trigger modal for new trigger
  const openNewTriggerModal = () => {
    setEditingTrigger(null);
    // Find the email collection flow if it exists
    const emailFlow = chatFlows.find(f => f.name === 'Email Collection Flow');
    setTriggerForm({
      keyword: '',
      match_type: 'contains',
      action_type: emailFlow ? 'start_flow' : 'send_message',
      dm_message: communityPage ? `Hey! Thanks for your interest! Here's your exclusive link: ${getCommunityLink()}` : '',
      chat_flow_id: emailFlow?.id || null,
      is_active: true
    });
    setShowTriggerModal(true);
  };

  // Open trigger modal for editing
  const openEditTriggerModal = (trigger) => {
    setEditingTrigger(trigger);
    setTriggerForm({
      keyword: trigger.keyword,
      match_type: trigger.match_type,
      action_type: trigger.action_type,
      dm_message: trigger.dm_message || '',
      chat_flow_id: trigger.chat_flow_id || null,
      is_active: trigger.is_active
    });
    setShowTriggerModal(true);
  };

  // Save trigger (create or update)
  const saveTrigger = async () => {
    if (!triggerForm.keyword.trim()) {
      toast.error('Please enter a keyword');
      return;
    }

    setSavingTrigger(true);
    try {
      if (editingTrigger) {
        // Update existing trigger
        await api.put(`/triggers/${editingTrigger.id}`, triggerForm, { params: { account_id: selectedAccountId } });
        toast.success('Trigger updated!');
      } else {
        // Create new trigger
        await api.post('/triggers', triggerForm, { params: { account_id: selectedAccountId } });
        toast.success('Trigger created!');
      }
      setShowTriggerModal(false);
      fetchTriggers();
    } catch (err) {
      console.error('Error saving trigger:', err);
      toast.error(err.response?.data?.detail || 'Failed to save trigger');
    } finally {
      setSavingTrigger(false);
    }
  };

  // Delete trigger
  const deleteTrigger = async (triggerId) => {
    if (!window.confirm('Are you sure you want to delete this trigger?')) return;

    try {
      await api.delete(`/triggers/${triggerId}`, { params: { account_id: selectedAccountId } });
      toast.success('Trigger deleted');
      fetchTriggers();
    } catch (err) {
      console.error('Error deleting trigger:', err);
      toast.error('Failed to delete trigger');
    }
  };

  // Toggle trigger active status
  const toggleTriggerActive = async (trigger) => {
    try {
      await api.put(`/triggers/${trigger.id}`, { is_active: !trigger.is_active }, { params: { account_id: selectedAccountId } });
      fetchTriggers();
    } catch (err) {
      console.error('Error toggling trigger:', err);
      toast.error('Failed to update trigger');
    }
  };

  // Check for permission granted on mount
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('comments_permission_granted') === 'true') {
      toast.success('Comments permission granted! Your engaged users are being fetched.');
      // Clean up the URL
      window.history.replaceState({}, document.title, window.location.pathname);
    }
    if (urlParams.get('messages_permission_granted') === 'true') {
      toast.success('Messages permission granted! You can now send automated DMs.');
      setHasMessagesPermission(true);
      // Clean up the URL
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, []);

  // Fetch accounts first
  useEffect(() => {
    const fetchAccounts = async () => {
      try {
        const response = await api.get('/accounts/instagram-accounts');
        const accountList = response.data.accounts || [];
        setAccounts(accountList);
        if (accountList.length > 0) {
          setSelectedAccountId(accountList[0].id);
          // Check if the first account has comments and messages permissions
          const firstAccount = accountList[0];
          setHasCommentsPermission(firstAccount.has_comments_permission !== false);
          setHasMessagesPermission(firstAccount.has_messages_permission === true);
        }
      } catch (err) {
        console.error('Error fetching accounts:', err);
        if (err.response?.status === 403) {
          setFeatureEnabled(false);
        }
      }
    };
    fetchAccounts();
  }, []);

  // Fetch data based on selected account
  const fetchData = useCallback(async () => {
    if (!selectedAccountId) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Fetch engaged users - handle gracefully if endpoint fails
      try {
        const engagedResponse = await api.get(`/accounts/instagram-accounts/${selectedAccountId}/engaged-users`, {
          params: { search: searchTerm, limit: 100 }
        });
        setEngagedUsers(engagedResponse.data.users || []);

        // Fetch engaged stats
        const engagedStatsResponse = await api.get(`/accounts/instagram-accounts/${selectedAccountId}/engaged-users/stats`);
        setEngagedStats(engagedStatsResponse.data);
      } catch (err) {
        console.log('Engaged users not available yet:', err.message);
        setEngagedUsers([]);
        setEngagedStats({ total_unique_users: 0, total_comments: 0, top_engagers: [] });
      }

      // Fetch community page
      try {
        const pageResponse = await api.get('/community/my-page');
        setCommunityPage(pageResponse.data);

        // Fetch community members
        const membersResponse = await api.get('/community/my-page/members', {
          params: { search: searchTerm, limit: 100 }
        });
        setCommunityMembers(membersResponse.data || []);

        // Fetch community stats
        const commStatsResponse = await api.get('/community/my-page/stats');
        setCommunityStats(commStatsResponse.data);
      } catch (err) {
        // Community page might not exist yet - that's OK
        console.log('Community page not set up yet');
        setCommunityPage(null);
        setCommunityMembers([]);
        setCommunityStats({ total_members: 0, conversion_rate: 0 });
      }

      setDataLoaded(true);
      setInitialLoadComplete(true);
    } catch (err) {
      console.error('Error fetching audience data:', err);
      if (err.response?.status === 403) {
        setFeatureEnabled(false);
      } else {
        setError('Failed to load some data. This feature is being set up.');
      }
      setInitialLoadComplete(true);
    } finally {
      setLoading(false);
    }
  }, [selectedAccountId, searchTerm]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleExport = async (type) => {
    try {
      let url;
      if (type === 'engaged') {
        url = `/accounts/instagram-accounts/${selectedAccountId}/engaged-users/export`;
      } else {
        url = '/community/my-page/members/export';
      }
      
      const response = await api.get(url, { responseType: 'blob' });
      const blob = new Blob([response.data], { type: 'text/csv' });
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = `${type}_export.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(downloadUrl);
      toast.success('Export downloaded successfully!');
    } catch (err) {
      console.error('Error exporting:', err);
      toast.error('Failed to export data. Please try again.');
    }
  };

  if (!featureEnabled) {
    return (
      <div className="p-6 max-w-4xl mx-auto">
        <div className="bg-gray-800/50 rounded-xl p-8 text-center border border-gray-700">
          <Users className="w-12 h-12 text-gray-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-white mb-2">Audience Features Not Available</h2>
          <p className="text-gray-400 mb-4">
            This feature is not enabled for your account. Please contact support or upgrade your plan.
          </p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="p-6 max-w-4xl mx-auto">
        <div className="flex items-center justify-center py-20">
          <div className="text-center">
            <Loader2 className="w-10 h-10 text-indigo-500 animate-spin mx-auto mb-4" />
            <p className="text-gray-400">Loading your audience data...</p>
          </div>
        </div>
      </div>
    );
  }

  // Check if this is a new user with no data - only after initial load completes
  const isNewUser = initialLoadComplete && !communityPage && engagedUsers.length === 0 && communityMembers.length === 0;

  return (
    <div className="p-6 max-w-6xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-white mb-2">Audience Manager</h1>
        <p className="text-gray-400">Keep track of your engaged followers - another layer of protection for your Instagram presence</p>
      </div>

      {/* Account Selector */}
      {accounts.length > 1 && (
        <div className="mb-6">
          <select
            value={selectedAccountId || ''}
            onChange={(e) => {
              const newAccountId = Number(e.target.value);
              setSelectedAccountId(newAccountId);
              // Update permissions for the newly selected account
              const selectedAcc = accounts.find(acc => acc.id === newAccountId);
              setHasCommentsPermission(selectedAcc?.has_comments_permission !== false);
              setHasMessagesPermission(selectedAcc?.has_messages_permission === true);
            }}
            className="bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
          >
            {accounts.map(acc => (
              <option key={acc.id} value={acc.id}>@{acc.username}</option>
            ))}
          </select>
        </div>
      )}

      {/* Welcome / Setup Card for new users */}
      {isNewUser && (
        <div className="bg-gradient-to-br from-indigo-600/20 to-purple-600/20 rounded-2xl p-8 mb-8 border border-indigo-500/30">
          <div className="flex items-start gap-4">
            <div className="p-3 bg-indigo-500/20 rounded-xl">
              <Sparkles className="w-8 h-8 text-indigo-400" />
            </div>
            <div className="flex-1">
              <h2 className="text-2xl font-bold text-white mb-2">Protect Your Audience Connection</h2>
              <p className="text-gray-300 mb-4">
                If your Instagram account gets suspended or hacked, you could lose contact with your followers forever. 
                Audience Manager helps you maintain a backup connection to your most engaged followers.
              </p>
              <div className="space-y-3 mb-6">
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-indigo-500/30 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-indigo-400 text-sm font-bold">1</span>
                  </div>
                  <p className="text-gray-300">
                    <strong className="text-white">Track Engaged Users</strong> - We automatically save usernames of people who comment on your posts
                  </p>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-indigo-500/30 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-indigo-400 text-sm font-bold">2</span>
                  </div>
                  <p className="text-gray-300">
                    <strong className="text-white">Collect Contact Info</strong> - Create a simple page where followers can share their email
                  </p>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-indigo-500/30 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-indigo-400 text-sm font-bold">3</span>
                  </div>
                  <p className="text-gray-300">
                    <strong className="text-white">Stay Connected</strong> - If anything happens to your account, you can still reach your audience
                  </p>
                </div>
              </div>
              <button
                onClick={createContactPage}
                disabled={creatingPage}
                className="inline-flex items-center gap-2 px-6 py-3 bg-indigo-600 hover:bg-indigo-700 disabled:bg-indigo-800 text-white rounded-lg font-medium transition-colors"
              >
                {creatingPage ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Creating...
                  </>
                ) : (
                  <>
                    Set Up Contact Page
                    <ArrowRight className="w-4 h-4" />
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Community Link Card - Show prominently if community page exists */}
      {communityPage && (
        <div className="bg-gradient-to-r from-indigo-600/20 to-purple-600/20 rounded-xl p-6 mb-8 border border-indigo-500/30">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-indigo-500/20 rounded-xl">
                <Link2 className="w-6 h-6 text-indigo-400" />
              </div>
              <div>
                <p className="text-white font-semibold text-lg mb-1">Your Follower Contact Page</p>
                <p className="text-gray-400 text-sm mb-2">Share this link so followers can give you their email - your backup connection if anything happens to your account</p>
                <div className="flex items-center gap-2">
                  <code className="bg-gray-800/80 px-3 py-1.5 rounded-lg text-indigo-300 text-sm font-mono">
                    {getCommunityLink()}
                  </code>
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${communityPage.is_active ? 'bg-green-500/20 text-green-400' : 'bg-yellow-500/20 text-yellow-400'}`}>
                    {communityPage.is_active ? 'Active' : 'Inactive'}
                  </span>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={copyLink}
                className={`flex items-center gap-2 px-4 py-2.5 rounded-lg font-medium transition-all ${
                  copied 
                    ? 'bg-green-600 text-white' 
                    : 'bg-indigo-600 hover:bg-indigo-700 text-white'
                }`}
              >
                {copied ? (
                  <>
                    <Check className="w-4 h-4" />
                    Copied!
                  </>
                ) : (
                  <>
                    <Copy className="w-4 h-4" />
                    Copy Link
                  </>
                )}
              </button>
              <a
                href={getCommunityLink()}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 px-4 py-2.5 bg-gray-700 hover:bg-gray-600 text-white rounded-lg font-medium transition-colors"
              >
                <ExternalLink className="w-4 h-4" />
                Preview
              </a>
              <button
                onClick={() => setActiveTab('settings')}
                className="p-2.5 text-gray-400 hover:text-white hover:bg-gray-700 rounded-lg transition-colors"
                title="Edit Settings"
              >
                <Settings className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
        <div className="bg-gray-800/50 rounded-xl p-5 border border-gray-700">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-400 text-sm mb-1">Engaged Users</p>
              <p className="text-3xl font-bold text-white">{engagedStats?.total_unique_users || 0}</p>
              <p className="text-gray-500 text-xs mt-1">Users who commented</p>
            </div>
            <div className="p-3 bg-indigo-500/20 rounded-xl">
              <MessageCircle className="w-6 h-6 text-indigo-400" />
            </div>
          </div>
        </div>

        <div className="bg-gray-800/50 rounded-xl p-5 border border-gray-700">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-400 text-sm mb-1">Total Comments</p>
              <p className="text-3xl font-bold text-white">{engagedStats?.total_comments || 0}</p>
              <p className="text-gray-500 text-xs mt-1">Across all posts</p>
            </div>
            <div className="p-3 bg-purple-500/20 rounded-xl">
              <BarChart2 className="w-6 h-6 text-purple-400" />
            </div>
          </div>
        </div>

        <div className="bg-gray-800/50 rounded-xl p-5 border border-gray-700">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-400 text-sm mb-1">Email Contacts</p>
              <p className="text-3xl font-bold text-white">{communityStats?.total_members || 0}</p>
              <p className="text-gray-500 text-xs mt-1">Backup contacts collected</p>
            </div>
            <div className="p-3 bg-green-500/20 rounded-xl">
              <Users className="w-6 h-6 text-green-400" />
            </div>
          </div>
        </div>

        <div className="bg-gray-800/50 rounded-xl p-5 border border-gray-700">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-400 text-sm mb-1">Protection Rate</p>
              <p className="text-3xl font-bold text-white">{communityStats?.conversion_rate || 0}%</p>
              <p className="text-gray-500 text-xs mt-1">Followers with backup contact</p>
            </div>
            <div className="p-3 bg-yellow-500/20 rounded-xl">
              <ChevronRight className="w-6 h-6 text-yellow-400" />
            </div>
          </div>
        </div>
      </div>

      {/* Error message if any */}
      {error && (
        <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4 mb-6 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-yellow-400 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-yellow-400 font-medium">Note</p>
            <p className="text-yellow-300/80 text-sm">{error}</p>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="flex border-b border-gray-700 mb-6">
        <button
          onClick={() => setActiveTab('overview')}
          className={`px-4 py-3 font-medium transition-colors relative ${
            activeTab === 'overview' 
              ? 'text-indigo-400' 
              : 'text-gray-400 hover:text-white'
          }`}
        >
          Overview
          {activeTab === 'overview' && (
            <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-indigo-500"></div>
          )}
        </button>
        <button
          onClick={() => setActiveTab('engaged')}
          className={`px-4 py-3 font-medium transition-colors relative ${
            activeTab === 'engaged' 
              ? 'text-indigo-400' 
              : 'text-gray-400 hover:text-white'
          }`}
        >
          Engaged Users
          {activeTab === 'engaged' && (
            <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-indigo-500"></div>
          )}
        </button>
        <button
          onClick={() => setActiveTab('community')}
          className={`px-4 py-3 font-medium transition-colors relative ${
            activeTab === 'community' 
              ? 'text-indigo-400' 
              : 'text-gray-400 hover:text-white'
          }`}
        >
          Email Contacts
          {activeTab === 'community' && (
            <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-indigo-500"></div>
          )}
        </button>
        <button
          onClick={() => setActiveTab('settings')}
          className={`px-4 py-3 font-medium transition-colors relative ${
            activeTab === 'settings' 
              ? 'text-indigo-400' 
              : 'text-gray-400 hover:text-white'
          }`}
        >
          Page Settings
          {activeTab === 'settings' && (
            <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-indigo-500"></div>
          )}
        </button>
        <button
          onClick={() => setActiveTab('automation')}
          className={`px-4 py-3 font-medium transition-colors relative flex items-center gap-2 ${
            activeTab === 'automation' 
              ? 'text-indigo-400' 
              : 'text-gray-400 hover:text-white'
          }`}
        >
          <Zap className="w-4 h-4" />
          DM Automation
          {activeTab === 'automation' && (
            <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-indigo-500"></div>
          )}
        </button>
      </div>

      {/* Overview Tab */}
      {activeTab === 'overview' && (
        <div className="space-y-6">
          {/* Why this matters */}
          <div className="bg-gradient-to-r from-orange-500/10 to-red-500/10 rounded-xl p-6 border border-orange-500/20">
            <h3 className="text-lg font-semibold text-white mb-2 flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-orange-400" />
              Why Audience Backup Matters
            </h3>
            <p className="text-gray-300">
              Instagram accounts can be suspended, hacked, or disabled without warning. When this happens, you lose all connection 
              to your followers. Vaulto's Audience Manager gives you a backup way to stay connected - so even if the worst happens, 
              you can still reach the people who matter most to your business.
            </p>
          </div>

          {/* How it works */}
          <div className="bg-gray-800/50 rounded-xl p-6 border border-gray-700">
            <h3 className="text-lg font-semibold text-white mb-4">How It Works</h3>
            <div className="grid md:grid-cols-3 gap-6">
              <div className="text-center">
                <div className="w-12 h-12 bg-indigo-500/20 rounded-xl flex items-center justify-center mx-auto mb-3">
                  <MessageCircle className="w-6 h-6 text-indigo-400" />
                </div>
                <h4 className="text-white font-medium mb-2">1. Track Engagers</h4>
                <p className="text-gray-400 text-sm">
                  We automatically save usernames of people who comment on your posts - your most engaged followers
                </p>
              </div>
              <div className="text-center">
                <div className="w-12 h-12 bg-purple-500/20 rounded-xl flex items-center justify-center mx-auto mb-3">
                  <Link2 className="w-6 h-6 text-purple-400" />
                </div>
                <h4 className="text-white font-medium mb-2">2. Collect Emails</h4>
                <p className="text-gray-400 text-sm">
                  Share your contact page link so followers can give you their email as a backup connection
                </p>
              </div>
              <div className="text-center">
                <div className="w-12 h-12 bg-green-500/20 rounded-xl flex items-center justify-center mx-auto mb-3">
                  <Download className="w-6 h-6 text-green-400" />
                </div>
                <h4 className="text-white font-medium mb-2">3. Stay Protected</h4>
                <p className="text-gray-400 text-sm">
                  Export your data anytime - if something happens to your account, you can still reach your audience
                </p>
              </div>
            </div>
          </div>

          {/* Top Engagers */}
          {engagedStats?.top_engagers?.length > 0 && (
            <div className="bg-gray-800/50 rounded-xl p-6 border border-gray-700">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-white">Top Engagers</h3>
                <button
                  onClick={() => setActiveTab('engaged')}
                  className="text-indigo-400 hover:text-indigo-300 text-sm font-medium flex items-center gap-1"
                >
                  View All <ChevronRight className="w-4 h-4" />
                </button>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                {engagedStats.top_engagers.slice(0, 5).map((user, index) => (
                  <a
                    key={index}
                    href={`https://instagram.com/${user.username}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="bg-gray-700/50 rounded-lg p-4 border border-gray-600 hover:border-indigo-500/50 transition-colors text-center"
                  >
                    <p className="text-white font-medium truncate mb-1">@{user.username}</p>
                    <p className="text-indigo-400 text-sm">{user.comments} comments</p>
                  </a>
                ))}
              </div>
            </div>
          )}

          {/* Quick Actions */}
          {!communityPage && (
            <div className="bg-gray-800/50 rounded-xl p-6 border border-gray-700">
              <h3 className="text-lg font-semibold text-white mb-2">Set Up Your Contact Page</h3>
              <p className="text-gray-400 mb-4">
                Create a simple page where your followers can share their email address. This gives you a direct line 
                of communication that doesn't depend on Instagram.
              </p>
              <button
                onClick={createContactPage}
                disabled={creatingPage}
                className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 disabled:bg-indigo-800 text-white rounded-lg font-medium transition-colors"
              >
                {creatingPage ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Creating...
                  </>
                ) : (
                  <>
                    <Settings className="w-4 h-4" />
                    Create Contact Page
                  </>
                )}
              </button>
            </div>
          )}
        </div>
      )}

      {/* Engaged Users Tab */}
      {activeTab === 'engaged' && (
        <>
          {/* Search and Export */}
          <div className="flex items-center justify-between mb-4">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-500" />
              <input
                type="text"
                placeholder="Search by username..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={backfillEngagedUsers}
                disabled={backfilling}
                className="flex items-center gap-2 px-4 py-2 bg-gray-700 hover:bg-gray-600 disabled:bg-gray-800 text-white rounded-lg transition-colors"
                title="Fetch comments from your recent posts to update engaged users"
              >
                {backfilling ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <ArrowRight className="w-4 h-4" />
                )}
                {backfilling ? 'Updating...' : 'Refresh from Posts'}
              </button>
              <button
                onClick={() => handleExport('engaged')}
                disabled={engagedUsers.length === 0}
                className="flex items-center gap-2 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white rounded-lg transition-colors"
              >
                <Download className="w-4 h-4" />
                Export CSV
              </button>
            </div>
          </div>
          
          {/* Permission prompt - show when comments permission not granted */}
          {(!hasCommentsPermission || needsReauth) && (
            <div className="bg-gradient-to-r from-purple-500/20 to-indigo-500/20 border border-purple-500/30 rounded-xl p-6 mb-6">
              <div className="flex items-start gap-4">
                <div className="p-3 bg-purple-600/30 rounded-lg">
                  <Users className="w-6 h-6 text-purple-300" />
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-white mb-2">Enable Engaged Users Tracking</h3>
                  <p className="text-gray-300 mb-4">
                    Grant the comments permission to see everyone who comments on your posts. 
                    Build a list of your most engaged followers and never lose touch with your community.
                  </p>
                  <ul className="text-gray-400 text-sm mb-4 space-y-1">
                    <li className="flex items-center gap-2">
                      <Check className="w-4 h-4 text-green-400" />
                      See all commenters across your posts
                    </li>
                    <li className="flex items-center gap-2">
                      <Check className="w-4 h-4 text-green-400" />
                      Track your most engaged followers
                    </li>
                    <li className="flex items-center gap-2">
                      <Check className="w-4 h-4 text-green-400" />
                      Automatically updated when new comments arrive
                    </li>
                  </ul>
                  <button
                    onClick={handleGrantCommentsPermission}
                    className="flex items-center gap-2 px-5 py-2.5 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-colors font-medium"
                  >
                    <Instagram className="w-5 h-5" />
                    Grant Comments Permission
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Info about data freshness */}
          {engagedUsers.length === 0 && hasCommentsPermission && !needsReauth && (
            <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4 mb-4 flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-blue-400 font-medium">No Engaged Users Yet</p>
                <p className="text-blue-300/80 text-sm">
                  Engaged users are tracked when: (1) someone comments on your posts with webhooks enabled, or 
                  (2) you click "Refresh from Posts" to scan your recent posts for comments.
                </p>
              </div>
            </div>
          )}

          <div className="bg-gray-800/50 rounded-xl border border-gray-700 overflow-hidden">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-700 bg-gray-800/50">
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-400">Username</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-400">Comments</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-400">First Seen</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-400">Last Seen</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-400">Profile</th>
                </tr>
              </thead>
              <tbody>
                {engagedUsers.length === 0 ? (
                  <tr>
                    <td colSpan="5" className="px-4 py-12 text-center">
                      <MessageCircle className="w-10 h-10 text-gray-600 mx-auto mb-3" />
                      <p className="text-gray-400 font-medium mb-1">No engaged users yet</p>
                      <p className="text-gray-500 text-sm">
                        When users comment on your Instagram posts, they'll appear here automatically.
                      </p>
                    </td>
                  </tr>
                ) : (
                  engagedUsers.map(user => (
                    <tr key={user.id} className="border-b border-gray-700/50 hover:bg-gray-700/20">
                      <td className="px-4 py-3">
                        <a
                          href={`https://instagram.com/${user.username}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-2 text-white hover:text-indigo-400 transition-colors"
                        >
                          <Instagram className="w-4 h-4 text-pink-500" />
                          @{user.username}
                        </a>
                      </td>
                      <td className="px-4 py-3 text-gray-300">{user.total_comments}</td>
                      <td className="px-4 py-3 text-gray-400 text-sm">
                        {user.first_seen_at ? new Date(user.first_seen_at).toLocaleDateString() : '-'}
                      </td>
                      <td className="px-4 py-3 text-gray-400 text-sm">
                        {user.last_seen_at ? new Date(user.last_seen_at).toLocaleDateString() : '-'}
                      </td>
                      <td className="px-4 py-3">
                        <a
                          href={`https://instagram.com/${user.username}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-indigo-400 hover:text-indigo-300"
                        >
                          <ExternalLink className="w-4 h-4" />
                        </a>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* Community Members Tab */}
      {activeTab === 'community' && (
        <>
          {/* Search and Export */}
          <div className="flex items-center justify-between mb-4">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-500" />
              <input
                type="text"
                placeholder="Search by username or email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>
            <button
              onClick={() => handleExport('community')}
              disabled={communityMembers.length === 0}
              className="flex items-center gap-2 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white rounded-lg transition-colors"
            >
              <Download className="w-4 h-4" />
              Export CSV
            </button>
          </div>

          {!communityPage ? (
            <div className="bg-gray-800/50 rounded-xl border border-gray-700 p-12 text-center">
              <Link2 className="w-12 h-12 text-gray-600 mx-auto mb-4" />
              <h3 className="text-white font-semibold text-lg mb-2">Contact Page Not Set Up</h3>
              <p className="text-gray-400 mb-6 max-w-md mx-auto">
                Create a simple page where followers can share their email with you. 
                This gives you a backup way to reach them if anything happens to your Instagram account.
              </p>
              <button
                onClick={createContactPage}
                disabled={creatingPage}
                className="inline-flex items-center gap-2 px-6 py-3 bg-indigo-600 hover:bg-indigo-700 disabled:bg-indigo-800 text-white rounded-lg font-medium transition-colors"
              >
                {creatingPage ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Creating...
                  </>
                ) : (
                  <>
                    <Settings className="w-4 h-4" />
                    Create Contact Page
                  </>
                )}
              </button>
            </div>
          ) : (
            <div className="bg-gray-800/50 rounded-xl border border-gray-700 overflow-hidden">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-700 bg-gray-800/50">
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-400">Instagram</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-400">Email</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-400">Name</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-400">Source</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-400">Joined</th>
                  </tr>
                </thead>
                <tbody>
                  {communityMembers.length === 0 ? (
                    <tr>
                      <td colSpan="5" className="px-4 py-12 text-center">
                        <Users className="w-10 h-10 text-gray-600 mx-auto mb-3" />
                        <p className="text-gray-400 font-medium mb-1">No contacts collected yet</p>
                        <p className="text-gray-500 text-sm">
                          Share your contact page link in your Instagram bio or stories to start collecting emails.
                        </p>
                      </td>
                    </tr>
                  ) : (
                    communityMembers.map(member => (
                      <tr key={member.id} className="border-b border-gray-700/50 hover:bg-gray-700/20">
                        <td className="px-4 py-3">
                          <a
                            href={`https://instagram.com/${member.instagram_username}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-2 text-white hover:text-indigo-400 transition-colors"
                          >
                            <Instagram className="w-4 h-4 text-pink-500" />
                            @{member.instagram_username}
                          </a>
                        </td>
                        <td className="px-4 py-3">
                          <a
                            href={`mailto:${member.email}`}
                            className="flex items-center gap-2 text-gray-300 hover:text-white transition-colors"
                          >
                            <Mail className="w-4 h-4 text-gray-500" />
                            {member.email}
                          </a>
                        </td>
                        <td className="px-4 py-3 text-gray-300">{member.name || '-'}</td>
                        <td className="px-4 py-3">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            member.source === 'dm_flow' 
                              ? 'bg-purple-500/20 text-purple-400'
                              : 'bg-blue-500/20 text-blue-400'
                          }`}>
                            {member.source === 'dm_flow' ? 'DM Flow' : 'Landing Page'}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-gray-400 text-sm">
                          {new Date(member.joined_at).toLocaleDateString()}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {/* Settings Tab */}
      {activeTab === 'settings' && (
        <div className="max-w-2xl">
          {!communityPage ? (
            <div className="bg-gray-800/50 rounded-xl border border-gray-700 p-12 text-center">
              <Link2 className="w-12 h-12 text-gray-600 mx-auto mb-4" />
              <h3 className="text-white font-semibold text-lg mb-2">Create Your Contact Page</h3>
              <p className="text-gray-400 mb-6 max-w-md mx-auto">
                Set up a simple landing page where your followers can share their email with you.
              </p>
              <button
                onClick={createContactPage}
                disabled={creatingPage}
                className="inline-flex items-center gap-2 px-6 py-3 bg-indigo-600 hover:bg-indigo-700 disabled:bg-indigo-800 text-white rounded-lg font-medium transition-colors"
              >
                {creatingPage ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Creating...
                  </>
                ) : (
                  <>
                    <Sparkles className="w-4 h-4" />
                    Create Contact Page
                  </>
                )}
              </button>
            </div>
          ) : (
            <div className="space-y-6">
              {/* Page Status */}
              <div className="bg-gray-800/50 rounded-xl p-6 border border-gray-700">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h3 className="text-lg font-semibold text-white">Page Status</h3>
                    <p className="text-gray-400 text-sm">Enable or disable your contact page</p>
                  </div>
                  <button
                    onClick={() => setSettingsForm(prev => ({ ...prev, is_active: !prev.is_active }))}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors ${
                      settingsForm.is_active 
                        ? 'bg-green-600 hover:bg-green-700 text-white' 
                        : 'bg-gray-700 hover:bg-gray-600 text-gray-300'
                    }`}
                  >
                    {settingsForm.is_active ? (
                      <>
                        <ToggleRight className="w-5 h-5" />
                        Active
                      </>
                    ) : (
                      <>
                        <ToggleLeft className="w-5 h-5" />
                        Inactive
                      </>
                    )}
                  </button>
                </div>
                {settingsForm.is_active && (
                  <div className="flex items-center gap-2 p-3 bg-green-500/10 border border-green-500/30 rounded-lg">
                    <Check className="w-4 h-4 text-green-400" />
                    <span className="text-green-400 text-sm">Your page is live and accepting signups</span>
                  </div>
                )}
              </div>

              {/* Page Link */}
              <div className="bg-gray-800/50 rounded-xl p-6 border border-gray-700">
                <h3 className="text-lg font-semibold text-white mb-2">Your Page Link</h3>
                <p className="text-gray-400 text-sm mb-4">Share this link in your Instagram bio, stories, or DMs</p>
                <div className="flex items-center gap-3">
                  <code className="flex-1 bg-gray-900 px-4 py-3 rounded-lg text-indigo-300 font-mono text-sm">
                    {getCommunityLink()}
                  </code>
                  <button
                    onClick={copyLink}
                    className={`flex items-center gap-2 px-4 py-3 rounded-lg font-medium transition-all ${
                      copied 
                        ? 'bg-green-600 text-white' 
                        : 'bg-indigo-600 hover:bg-indigo-700 text-white'
                    }`}
                  >
                    {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                    {copied ? 'Copied!' : 'Copy'}
                  </button>
                </div>
              </div>

              {/* Page Content */}
              <div className="bg-gray-800/50 rounded-xl p-6 border border-gray-700">
                <h3 className="text-lg font-semibold text-white mb-4">Page Content</h3>
                
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Headline
                    </label>
                    <input
                      type="text"
                      value={settingsForm.headline}
                      onChange={(e) => setSettingsForm(prev => ({ ...prev, headline: e.target.value }))}
                      placeholder="e.g., Get exclusive updates!"
                      className="w-full px-4 py-3 bg-gray-900 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Description (optional)
                    </label>
                    <textarea
                      value={settingsForm.description}
                      onChange={(e) => setSettingsForm(prev => ({ ...prev, description: e.target.value }))}
                      placeholder="Tell your followers why they should sign up..."
                      rows={3}
                      className="w-full px-4 py-3 bg-gray-900 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 resize-none"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Button Text
                    </label>
                    <input
                      type="text"
                      value={settingsForm.button_text}
                      onChange={(e) => setSettingsForm(prev => ({ ...prev, button_text: e.target.value }))}
                      placeholder="e.g., Join, Subscribe, Sign Up"
                      className="w-full px-4 py-3 bg-gray-900 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>
                </div>
              </div>

              {/* Save Button */}
              <div className="flex items-center justify-between">
                <a
                  href={getCommunityLink()}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 text-indigo-400 hover:text-indigo-300 font-medium"
                >
                  <ExternalLink className="w-4 h-4" />
                  Preview Page
                </a>
                <button
                  onClick={saveSettings}
                  disabled={savingSettings}
                  className="flex items-center gap-2 px-6 py-3 bg-indigo-600 hover:bg-indigo-700 disabled:bg-indigo-800 text-white rounded-lg font-medium transition-colors"
                >
                  {savingSettings ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4" />
                      Save Changes
                    </>
                  )}
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* DM Automation Tab */}
      {activeTab === 'automation' && (
        <div className="space-y-6">
          {/* Intro Card */}
          <div className="bg-gradient-to-r from-purple-500/10 to-pink-500/10 rounded-xl p-6 border border-purple-500/20">
            <div className="flex items-start gap-4">
              <div className="p-3 bg-purple-500/20 rounded-xl">
                <Zap className="w-6 h-6 text-purple-400" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-white mb-2">DM Automation</h3>
                <p className="text-gray-300">
                  When someone comments a specific keyword on your posts, automatically send them a DM with your 
                  contact page link. This helps you collect emails from engaged followers effortlessly.
                </p>
              </div>
            </div>
          </div>

          {/* Messages Permission Prompt - show when not granted */}
          {!hasMessagesPermission && (
            <div className="bg-gradient-to-r from-purple-500/20 to-pink-500/20 border border-purple-500/30 rounded-xl p-6">
              <div className="flex items-start gap-4">
                <div className="p-3 bg-purple-600/30 rounded-lg">
                  <Send className="w-6 h-6 text-purple-300" />
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-white mb-2">Enable DM Automation</h3>
                  <p className="text-gray-300 mb-4">
                    To send automated DMs when followers comment on your posts, you need to grant the messages permission. 
                    This allows Vaulto to send DMs on your behalf.
                  </p>
                  <ul className="text-gray-400 text-sm mb-4 space-y-1">
                    <li className="flex items-center gap-2">
                      <Check className="w-4 h-4 text-green-400" />
                      Automatically reply to comments with DMs
                    </li>
                    <li className="flex items-center gap-2">
                      <Check className="w-4 h-4 text-green-400" />
                      Collect emails through conversational flows
                    </li>
                    <li className="flex items-center gap-2">
                      <Check className="w-4 h-4 text-green-400" />
                      Send your contact page link automatically
                    </li>
                  </ul>
                  <button
                    onClick={handleGrantMessagesPermission}
                    className="flex items-center gap-2 px-5 py-2.5 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-colors font-medium"
                  >
                    <Instagram className="w-5 h-5" />
                    Grant Messages Permission
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Triggers List */}
          <div className="bg-gray-800/50 rounded-xl border border-gray-700">
            <div className="flex items-center justify-between p-4 border-b border-gray-700">
              <h3 className="text-lg font-semibold text-white">Keyword Triggers</h3>
              <button
                onClick={hasMessagesPermission ? openNewTriggerModal : handleGrantMessagesPermission}
                className="flex items-center gap-2 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg font-medium transition-colors"
              >
                <Plus className="w-4 h-4" />
                New Trigger
              </button>
            </div>

            {triggersLoading ? (
              <div className="p-8 text-center">
                <Loader2 className="w-8 h-8 text-indigo-500 animate-spin mx-auto mb-2" />
                <p className="text-gray-400">Loading triggers...</p>
              </div>
            ) : triggers.length === 0 ? (
              <div className="p-8 text-center">
                <Zap className="w-12 h-12 text-gray-600 mx-auto mb-4" />
                <h4 className="text-white font-semibold mb-2">No Triggers Yet</h4>
                <p className="text-gray-400 mb-4 max-w-md mx-auto">
                  {hasMessagesPermission 
                    ? 'Create your first keyword trigger to automatically DM followers who comment on your posts.'
                    : 'Grant the messages permission above to start creating DM automation triggers.'}
                </p>
                {hasMessagesPermission && (
                  <button
                    onClick={openNewTriggerModal}
                    className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg font-medium transition-colors"
                  >
                    <Plus className="w-4 h-4" />
                    Create First Trigger
                  </button>
                )}
              </div>
            ) : (
              <div className="divide-y divide-gray-700">
                {triggers.map((trigger) => (
                  <div key={trigger.id} className="p-4 flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <button
                        onClick={() => toggleTriggerActive(trigger)}
                        className={`p-2 rounded-lg transition-colors ${
                          trigger.is_active 
                            ? 'bg-green-500/20 text-green-400' 
                            : 'bg-gray-700 text-gray-500'
                        }`}
                      >
                        {trigger.is_active ? (
                          <ToggleRight className="w-5 h-5" />
                        ) : (
                          <ToggleLeft className="w-5 h-5" />
                        )}
                      </button>
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="text-white font-medium">"{trigger.keyword}"</span>
                          <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                            trigger.match_type === 'exact' 
                              ? 'bg-blue-500/20 text-blue-400'
                              : trigger.match_type === 'starts_with'
                              ? 'bg-purple-500/20 text-purple-400'
                              : 'bg-gray-700 text-gray-400'
                          }`}>
                            {trigger.match_type}
                          </span>
                          {trigger.is_active && (
                            <span className="px-2 py-0.5 rounded text-xs font-medium bg-green-500/20 text-green-400">
                              Active
                            </span>
                          )}
                        </div>
                        <p className="text-gray-400 text-sm mt-1">
                          {trigger.action_type === 'start_flow' ? (
                            <span className="text-purple-400">Collects email via chat flow</span>
                          ) : trigger.action_type === 'send_link' ? (
                            'Sends contact page link'
                          ) : (
                            'Sends custom message'
                          )}
                          {trigger.times_triggered > 0 && (
                            <span className="ml-2 text-gray-500">
                              • Triggered {trigger.times_triggered} times
                            </span>
                          )}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => openEditTriggerModal(trigger)}
                        className="p-2 text-gray-400 hover:text-white hover:bg-gray-700 rounded-lg transition-colors"
                      >
                        <Edit2 className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => deleteTrigger(trigger.id)}
                        className="p-2 text-gray-400 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-colors"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* How it works */}
          <div className="bg-gray-800/50 rounded-xl p-6 border border-gray-700">
            <h3 className="text-lg font-semibold text-white mb-4">How It Works</h3>
            <div className="grid md:grid-cols-3 gap-6">
              <div className="text-center">
                <div className="w-12 h-12 bg-pink-500/20 rounded-xl flex items-center justify-center mx-auto mb-3">
                  <MessageCircle className="w-6 h-6 text-pink-400" />
                </div>
                <h4 className="text-white font-medium mb-2">1. User Comments Keyword</h4>
                <p className="text-gray-400 text-sm">
                  A follower comments your trigger word (e.g., "LINK") on one of your posts
                </p>
              </div>
              <div className="text-center">
                <div className="w-12 h-12 bg-purple-500/20 rounded-xl flex items-center justify-center mx-auto mb-3">
                  <Send className="w-6 h-6 text-purple-400" />
                </div>
                <h4 className="text-white font-medium mb-2">2. Auto-DM Sent</h4>
                <p className="text-gray-400 text-sm">
                  Vaulto automatically sends them a DM with your contact page link
                </p>
              </div>
              <div className="text-center">
                <div className="w-12 h-12 bg-green-500/20 rounded-xl flex items-center justify-center mx-auto mb-3">
                  <Mail className="w-6 h-6 text-green-400" />
                </div>
                <h4 className="text-white font-medium mb-2">3. Email Collected</h4>
                <p className="text-gray-400 text-sm">
                  They visit your page, enter their email, and you have a backup contact
                </p>
              </div>
            </div>
          </div>

          {!communityPage && (
            <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4 flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-yellow-400 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-yellow-400 font-medium">Set Up Your Contact Page First</p>
                <p className="text-yellow-300/80 text-sm">
                  You need to create and enable your contact page before DM automation can work.
                  <button
                    onClick={() => setActiveTab('settings')}
                    className="ml-2 underline hover:no-underline"
                  >
                    Go to Page Settings
                  </button>
                </p>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Trigger Modal */}
      {showTriggerModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 rounded-xl border border-gray-700 w-full max-w-lg">
            <div className="flex items-center justify-between p-4 border-b border-gray-700">
              <h3 className="text-lg font-semibold text-white">
                {editingTrigger ? 'Edit Trigger' : 'Create New Trigger'}
              </h3>
              <button
                onClick={() => setShowTriggerModal(false)}
                className="p-2 text-gray-400 hover:text-white hover:bg-gray-700 rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-4 space-y-4">
              {/* Keyword */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Trigger Keyword
                </label>
                <input
                  type="text"
                  value={triggerForm.keyword}
                  onChange={(e) => setTriggerForm(prev => ({ ...prev, keyword: e.target.value }))}
                  placeholder="e.g., LINK, INFO, FREE"
                  className="w-full px-4 py-3 bg-gray-900 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
                <p className="text-gray-500 text-xs mt-1">
                  When someone comments this word, they'll receive a DM
                </p>
              </div>

              {/* Match Type */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Match Type
                </label>
                <div className="grid grid-cols-3 gap-2">
                  {[
                    { value: 'contains', label: 'Contains' },
                    { value: 'exact', label: 'Exact Match' },
                    { value: 'starts_with', label: 'Starts With' }
                  ].map((opt) => (
                    <button
                      key={opt.value}
                      onClick={() => setTriggerForm(prev => ({ ...prev, match_type: opt.value }))}
                      className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                        triggerForm.match_type === opt.value
                          ? 'bg-indigo-600 text-white'
                          : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                      }`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Action Type */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  When triggered, what should happen?
                </label>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    onClick={() => setTriggerForm(prev => ({ ...prev, action_type: 'send_message', chat_flow_id: null }))}
                    className={`px-3 py-3 rounded-lg text-sm font-medium transition-colors text-left ${
                      triggerForm.action_type === 'send_message'
                        ? 'bg-indigo-600 text-white'
                        : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                    }`}
                  >
                    <Send className="w-4 h-4 mb-1" />
                    <div>Send Message</div>
                    <div className="text-xs opacity-70">Send a single DM with your link</div>
                  </button>
                  <button
                    onClick={() => {
                      const emailFlow = chatFlows.find(f => f.name === 'Email Collection Flow');
                      setTriggerForm(prev => ({ 
                        ...prev, 
                        action_type: 'start_flow',
                        chat_flow_id: emailFlow?.id || null
                      }));
                    }}
                    className={`px-3 py-3 rounded-lg text-sm font-medium transition-colors text-left ${
                      triggerForm.action_type === 'start_flow'
                        ? 'bg-indigo-600 text-white'
                        : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                    }`}
                  >
                    <Mail className="w-4 h-4 mb-1" />
                    <div>Collect Email</div>
                    <div className="text-xs opacity-70">Ask for email in DM conversation</div>
                  </button>
                </div>
              </div>

              {/* Email Flow Setup (for start_flow action) */}
              {triggerForm.action_type === 'start_flow' && (
                <div className="bg-purple-500/10 border border-purple-500/30 rounded-lg p-4">
                  {emailFlowCreated || chatFlows.some(f => f.name === 'Email Collection Flow') ? (
                    <div className="flex items-center gap-3">
                      <Check className="w-5 h-5 text-green-400" />
                      <div>
                        <p className="text-white font-medium">Email Collection Flow Ready</p>
                        <p className="text-gray-400 text-sm">
                          When triggered, bot will ask for email → validate → save to contacts
                        </p>
                      </div>
                    </div>
                  ) : (
                    <div>
                      <p className="text-white font-medium mb-2">Create Email Collection Flow</p>
                      <p className="text-gray-400 text-sm mb-3">
                        This will create a 2-step conversation that asks for the user's email and saves it automatically.
                      </p>
                      <button
                        onClick={createEmailFlow}
                        disabled={creatingEmailFlow}
                        className="flex items-center gap-2 px-4 py-2 bg-purple-600 hover:bg-purple-700 disabled:bg-purple-800 text-white rounded-lg font-medium transition-colors"
                      >
                        {creatingEmailFlow ? (
                          <>
                            <Loader2 className="w-4 h-4 animate-spin" />
                            Creating...
                          </>
                        ) : (
                          <>
                            <Sparkles className="w-4 h-4" />
                            Create Email Flow
                          </>
                        )}
                      </button>
                    </div>
                  )}
                </div>
              )}

              {/* DM Message (for send_message action) */}
              {triggerForm.action_type === 'send_message' && (
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    DM Message
                  </label>
                  <textarea
                    value={triggerForm.dm_message}
                    onChange={(e) => setTriggerForm(prev => ({ ...prev, dm_message: e.target.value }))}
                    placeholder="Hey! Thanks for your interest! Here's your exclusive link..."
                    rows={4}
                    className="w-full px-4 py-3 bg-gray-900 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 resize-none"
                  />
                  {communityPage && (
                    <button
                      onClick={() => setTriggerForm(prev => ({ 
                        ...prev, 
                        dm_message: `Hey! Thanks for your interest! Here's your exclusive link: ${getCommunityLink()}`
                      }))}
                      className="text-indigo-400 hover:text-indigo-300 text-sm mt-2"
                    >
                      + Insert contact page link
                    </button>
                  )}
                </div>
              )}

              {/* Active Toggle */}
              <div className="flex items-center justify-between py-2">
                <span className="text-gray-300">Trigger Active</span>
                <button
                  onClick={() => setTriggerForm(prev => ({ ...prev, is_active: !prev.is_active }))}
                  className={`flex items-center gap-2 px-3 py-1.5 rounded-lg font-medium transition-colors ${
                    triggerForm.is_active 
                      ? 'bg-green-600 text-white' 
                      : 'bg-gray-700 text-gray-400'
                  }`}
                >
                  {triggerForm.is_active ? (
                    <>
                      <ToggleRight className="w-4 h-4" />
                      On
                    </>
                  ) : (
                    <>
                      <ToggleLeft className="w-4 h-4" />
                      Off
                    </>
                  )}
                </button>
              </div>
            </div>

            <div className="flex items-center justify-end gap-3 p-4 border-t border-gray-700">
              <button
                onClick={() => setShowTriggerModal(false)}
                className="px-4 py-2 text-gray-400 hover:text-white transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={saveTrigger}
                disabled={savingTrigger || !triggerForm.keyword.trim()}
                className="flex items-center gap-2 px-6 py-2 bg-indigo-600 hover:bg-indigo-700 disabled:bg-gray-700 disabled:text-gray-500 text-white rounded-lg font-medium transition-colors"
              >
                {savingTrigger ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4" />
                    {editingTrigger ? 'Update Trigger' : 'Create Trigger'}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
